"use client";

import { AnimatePresence, motion, type MotionProps } from "motion/react";
import {
  type FC,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import type { Timeout } from "../../types/Timeout";

import { AVATAR_ANIMATIONS } from "./AgentAvatar.motion";

export type AgentAvatarGradientColors = [string, string, string];

export type AgentAvatarProps = {
  animationType?: keyof typeof AVATAR_ANIMATIONS;
  size?: number;
  layers?: number;
  isListening?: boolean;
  durationMultiplier?: number;
  easing?: [number, number, number, number];
  /** Gradient colors: [start, middle, end] */
  gradientColors?: AgentAvatarGradientColors;
};

const DEFAULT_GRADIENT_COLORS: AgentAvatarGradientColors = [
  "#0E83FF",
  "#C467D5",
  "#FF6B6E",
];

export const AgentAvatar: FC<AgentAvatarProps> = ({
  animationType = "idle",
  size = 200,
  layers = 4,
  isListening,
  durationMultiplier = 0.5,
  easing = [0.8, 0, 0.2, 1],
  gradientColors = DEFAULT_GRADIENT_COLORS,
}) => {
  const gradientId = useId();
  const [currentAnimation, setCurrentAnimation] = useState(animationType);
  const [isInitialComplete, setIsInitialComplete] = useState(false);
  const [listeningSizes, setListeningSizes] = useState<number[] | undefined>(
    [],
  );
  const { initial, animate } = AVATAR_ANIMATIONS[currentAnimation];
  const baseSize = 400;
  const baseRadius = baseSize / 2;
  const layerCount = layers;

  const timeoutRef = useRef<Timeout>(undefined);
  const prevSizeRef = useRef(size);

  // NOTE: the avatar doesn't have any logic for processing
  // events. important thing to note is that for every event
  // received, the avatar will animate randomly. So the events
  // must come with some kind of rhythm to give the avatar
  // a chance to animate.
  const handleAudioEvent = useCallback(() => {
    if (!isInitialComplete) {
      return;
    }

    clearTimeout(timeoutRef.current);

    const base = baseRadius / layerCount;

    setListeningSizes(
      Array.from({ length: layerCount })
        .map((_, index) => {
          const i = index + 1;
          const min = base * i * 0.8;
          const max = base * (i + 0.1);

          return min + Math.random() * (max - min);
        })
        .reverse(),
    );

    timeoutRef.current = setTimeout(() => {
      setListeningSizes(undefined);
    }, 1000);
  }, [baseRadius, layerCount, isInitialComplete]);

  useEffect(() => {
    setCurrentAnimation(isListening ? "idle" : animationType);
    setIsInitialComplete(!!isListening);

    if (!isListening) {
      setListeningSizes(undefined);
    }
  }, [animationType, isListening]);

  useEffect(() => {
    addEventListener("avatarAudioEvent", handleAudioEvent as EventListener);

    return () => {
      removeEventListener(
        "avatarAudioEvent",
        handleAudioEvent as EventListener,
      );

      clearTimeout(timeoutRef.current);
    };
  }, [handleAudioEvent]);

  useEffect(() => {
    prevSizeRef.current = size;
  }, [size]);

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: "relative",
      }}
    >
      <div
        style={{
          transform: `translate(-50%, -50%) scale(${size / 400})`,
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "400px",
          height: "400px",
        }}
      >
        <svg width="400" height="400" viewBox="0 0 400 400" aria-hidden="true">
          {Array.from({ length: layerCount }).map((_, index) => {
            const isCircumscribed = currentAnimation === "layerRotate";

            const radius =
              baseRadius *
              (isCircumscribed
                ? (1 / Math.sqrt(2)) ** index
                : (layerCount - index) / layerCount);

            const animation = (
              isInitialComplete
                ? typeof animate === "function"
                  ? animate({ index, layerCount, size })
                  : animate
                : initial
            ) as MotionProps;

            const layerRadius = listeningSizes?.[index]
              ? listeningSizes[index]
              : radius * 2;

            const transition = animation.transition;

            const { delayPerLayer = 0 } = transition as any;
            const sharedLayerMotion = {};

            const layerSize = layerRadius * 2;
            const duration = (transition as any)?.duration * durationMultiplier;
            const delay = index * delayPerLayer * durationMultiplier;

            const sharedLayerProps = {
              opacity: 0,
            };

            const scale = 1 / (1100 / layerSize);

            return (
              <motion.g
                key={`huubAvatarLayer-${index}`}
                initial={{
                  rotate: 0,
                  x: 0,
                  y: 0,
                  scale: 1,
                }}
                animate={
                  {
                    ...animation,
                    transition: {
                      ease: easing,
                      ...(animation as any).transition,
                      duration,
                      delay,
                    },
                  } as any
                }
                onAnimationComplete={() => {
                  if (index === layerCount - 1 && !isInitialComplete) {
                    setTimeout(() => setIsInitialComplete(true), 400);
                  }
                }}
              >
                <AnimatePresence>
                  {index < layerCount && (
                    <motion.path
                      d="m 393.57539,226.74551 c 0.10485,-31.17299 -0.79573,-49.21208 -1.85493,-59.64575 -1.39523,-13.74648 -4.02708,-24.0698 -5.16963,-28.77892 -3.07542,-12.66034 -9.34187,-24.25515 -18.85044,-33.26406 -2.05656,-1.94496 -5.83903,-5.610475 -13.94696,-12.360876 C 347.2477,87.274239 335.0132,77.595698 312.05233,60.322533 277.8516,34.578468 257.1481,22.018044 247.09004,16.475363 241.98924,13.668265 235.30824,9.7605592 233.06054,8.513704 222.82399,2.8392405 211.43387,0 200.04376,0 c -11.39011,0 -22.78372,2.8392405 -33.01678,8.5102221 -2.2477,1.246855 -8.93219,5.1545609 -14.0295,7.9613919 C 142.93943,22.01108 122.23216,34.574986 88.03387,60.318972 65.075403,77.592134 52.839279,87.27065 46.333294,92.69234 38.22588,99.446305 34.443156,103.10826 32.38743,105.05322 c -9.509235,9.00872 -15.77416,20.60728 -18.850665,33.26387 -1.143205,4.70939 -3.7757349,15.03271 -5.1706521,28.77919 -1.062798,10.43715 -1.961282,28.47276 -1.856401,59.64923 h -0.024472 c 0.143338,42.58967 1.828432,68.91802 3.1848982,83.35929 0.9439199,10.05601 2.2374559,23.6565 3.1953819,28.08814 4.988864,23.07284 22.283854,41.67522 44.819176,48.90188 6.572656,2.10801 22.193098,5.91689 40.372544,8.45076 16.21106,2.25802 47.5705,4.4062 101.65801,4.45442 0.11183,0 0.22017,0 0.32851,0 0.11184,0 0.22017,0 0.32851,0 54.08751,-0.0482 85.45019,-2.20175 101.65802,-4.45977 18.17835,-2.53122 33.79747,-6.34006 40.37307,-8.44807 22.53614,-7.22936 39.83006,-25.8344 44.81956,-48.90298 0.95705,-4.43136 2.25012,-18.03186 3.1937,-28.08787 1.35761,-14.44127 3.0378,-40.76961 3.18566,-83.35929 h -0.0241 z"
                      fill={`url(#${gradientId})`}
                      initial={{
                        ...sharedLayerProps,
                        scale: 0,
                      }}
                      animate={{
                        ...sharedLayerProps,
                        scale,
                        opacity: Math.max(
                          0.1,
                          Math.min(1, 1 - 0.2 * layerCount + index * 0.1),
                        ),
                        transition: {
                          scale: {
                            duration: 0.5,
                            delay: index * 0.025,
                            ease: easing,
                          },
                          ...sharedLayerMotion,
                        },
                      }}
                      exit={{
                        scale: 0,
                        opacity: 0,
                        transition: {
                          scale: {
                            duration: 0.5,
                            ease: easing,
                          },
                          opacity: {
                            duration: 0.25,
                          },
                          ...sharedLayerMotion,
                        },
                      }}
                    />
                  )}
                </AnimatePresence>
              </motion.g>
            );
          })}
          <defs>
            <linearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="-10%" stopColor={gradientColors[0]} />
              <stop offset="50%" stopColor={gradientColors[1]} />
              <stop offset="110%" stopColor={gradientColors[2]} />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};
